import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { StepperComponent, StepperStep } from '@siemens/component-lib';
import { select } from '@ngxs/store';
import { WorkflowValidationStatus } from '@monorepo/interfaces';
import { WorkflowDataState } from '../../state/workflow/workflow-data.state';
import { WorkflowTransitionPhase } from './workflow-transition-phase.enum';

@Component({
    selector: 'app-workflow-transition-stepper',
    imports: [StepperComponent, TranslatePipe],
    templateUrl: './workflow-transition-stepper.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowTransitionStepperComponent {
    private readonly translateService = inject(TranslateService);
    private readonly currentLang = toSignal(this.translateService.onLangChange);

    public readonly phase = input.required<WorkflowTransitionPhase>();

    protected readonly validationJob = select(WorkflowDataState.validationJob);

    protected readonly showValidationProgress = computed(() => {
        return this.phase() === WorkflowTransitionPhase.VALIDATING && !!this.validationJob();
    });

    protected readonly validationPercentage = computed(() => {
        const job = this.validationJob();
        if (!job) return 0;
        if (job.status === WorkflowValidationStatus.SUCCEEDED) return 100;
        if (job.totalProducts <= 0) return 0;
        return Math.min(100, Math.round((job.processedProducts / job.totalProducts) * 100));
    });

    protected readonly isDeterminate = computed(() => {
        const job = this.validationJob();
        return !!job && job.totalProducts > 0;
    });

    protected readonly steps = computed<StepperStep[]>(() => {
        this.currentLang();
        const phase = this.phase();
        const savingLabel = this.translateService.instant('workflow-transition-stepper.saving');
        const validationLabel = this.translateService.instant('workflow-transition-stepper.validation');
        const successLabel = this.translateService.instant('workflow-transition-stepper.success');

        return [
            {
                label: phase === WorkflowTransitionPhase.SAVING ? `${savingLabel}...` : savingLabel,
                doneIcon: 'checkmark-large'
            },
            {
                label: phase === WorkflowTransitionPhase.VALIDATING ? `${validationLabel}...` : validationLabel,
                doneIcon: 'checkmark-large'
            },
            {
                label: successLabel,
                doneIcon: 'checkmark-large'
            }
        ];
    });

    protected readonly activeStepIndex = computed<number>(() => {
        switch (this.phase()) {
            case WorkflowTransitionPhase.SAVING:
            case WorkflowTransitionPhase.SAVE_FAILED:
                return 0;
            case WorkflowTransitionPhase.VALIDATING:
            case WorkflowTransitionPhase.VALIDATION_FAILED:
                return 1;
            case WorkflowTransitionPhase.SUCCESS:
                return 2;
            default:
                return 0;
        }
    });

    protected readonly invalidSteps = computed<number[]>(() => {
        switch (this.phase()) {
            case WorkflowTransitionPhase.SAVE_FAILED:
                return [0];
            case WorkflowTransitionPhase.VALIDATION_FAILED:
                return [1];
            default:
                return [];
        }
    });
}
